import { Cube, Storefront } from "@phosphor-icons/react";
import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Skeleton } from "../../feedback/Skeleton";

export type BusinessInfoChip = {
  label?: ReactNode;
  icon?: ReactNode;
};

export type BusinessInfoItem = {
  label: ReactNode;
  value?: ReactNode;
};

export type BusinessInfoSection = {
  title?: ReactNode;
  items: BusinessInfoItem[];
};

export type BusinessInfoDisplayCardProps = HTMLAttributes<HTMLDivElement> & {
  businessName: ReactNode;
  subtitle?: ReactNode;
  initials?: string;
  logoSrc?: string;
  logoAlt?: string;
  chips?: BusinessInfoChip[];
  sections: BusinessInfoSection[];
  background?: string;
  gradient?: string;
  headerBackground?: string;
  headerGradient?: string;
};

const defaultBusinessInfoChips: BusinessInfoChip[] = [
  { label: "30-100/ month", icon: <Cube size={14} weight="duotone" /> },
  { label: "Starting Up", icon: <Storefront size={14} weight="duotone" /> }
];

const DEFAULT_BUSINESS_INFO_SKELETON_ROWS = 6;
/* Widths of the original bfrs-w-24 / bfrs-w-28 / bfrs-w-20 / bfrs-w-32 / bfrs-w-16 / bfrs-w-36 cycle. */
const skeletonValueWidths = ["6rem", "7rem", "5rem", "8rem", "4rem", "9rem"] as const;
/* Widths of the original bfrs-w-[118px] / bfrs-w-[98px] cycle. */
const chipSkeletonWidths = ["118px", "98px"] as const;

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim
 * from Card.tsx / the frozen baseline). */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

const BusinessCardRoot = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-root"
})({
  ...fadedCardBorder,
  position: "relative",
  width: "100%",
  maxWidth: "354px",
  overflow: "hidden",
  borderBottomRightRadius: "10px",
  borderBottomLeftRadius: "10px",
  borderTopLeftRadius: "var(--bfrs-radius-xl)",
  borderTopRightRadius: "var(--bfrs-radius-xl)",
  backgroundImage: "var(--bfrs-business-card-background)",
  padding: 0,
  "--tw-shadow": "var(--bfrs-shadow-card)",
  "--tw-shadow-colored": "var(--bfrs-shadow-card)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const BusinessCardHeader = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-header"
})({
  display: "flex",
  minHeight: "174px",
  flexDirection: "column",
  alignItems: "center",
  paddingLeft: "1.5rem",
  paddingRight: "1.5rem",
  paddingTop: "25px",
  textAlign: "center",
  backgroundImage: "var(--business-card-header-background)"
});

/* `&&` overrides beat Skeleton's own variant rules, reproducing the old
 * tailwind-merge resolution of the className overrides. */

const BusinessCardAvatarSkeleton = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCard-avatarSkeleton"
})({
  "&&": {
    marginBottom: "8px",
    width: "37px",
    height: "37px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardLogoBox = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-logoBox"
})({
  marginBottom: "8px",
  display: "flex",
  width: "37px",
  height: "37px",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "18.66px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const BusinessCardLogoImage = styled("img", {
  label: "AgenticBusinessInfoDisplayCard-logoImage"
})({
  width: "28px",
  height: "28px",
  borderRadius: "var(--bfrs-radius-pill)",
  OObjectFit: "cover",
  objectFit: "cover"
});

const BusinessCardInitials = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-initials"
})({
  fontSize: "14.3px",
  fontWeight: 700,
  lineHeight: 1,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary-bright) / var(--tw-text-opacity, 1))"
});

const BusinessCardSrOnly = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-srOnly"
})({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0
});

const BusinessCardTitleGroup = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-titleGroup"
})({
  display: "grid",
  justifyItems: "center",
  gap: "2px"
});

const BusinessCardName = styled("h3", {
  label: "AgenticBusinessInfoDisplayCard-name"
})({
  maxWidth: "220px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "16.667px",
  fontWeight: 700,
  lineHeight: 1.25,
  color: "var(--bfrs-dcl-color-business-card-text)"
});

const BusinessCardNameSkeleton = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCard-nameSkeleton"
})({
  "&&": {
    height: "1.25rem",
    width: "8rem",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardSubtitleSkeleton = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCard-subtitleSkeleton"
})({
  "&&": {
    marginTop: ".25rem",
    height: "1rem",
    width: "10rem",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardSubtitle = styled("p", {
  label: "AgenticBusinessInfoDisplayCard-subtitle"
})({
  maxWidth: "240px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "13.333px",
  fontStyle: "italic",
  lineHeight: 1.25,
  color: "var(--bfrs-dcl-color-business-card-subtitle)"
});

const BusinessCardChipsLoadingRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-chipsLoadingRow"
})({
  marginTop: "18px",
  display: "flex",
  gap: ".5rem"
});

const BusinessCardChipSkeleton = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCard-chipSkeleton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { width: string } }>(({ ownerState }) => ({
  "&&": {
    height: "1.75rem",
    width: ownerState.width,
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
}));

const BusinessCardChipsRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-chipsRow"
})({
  marginTop: "18px",
  display: "flex",
  maxWidth: "100%",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: ".5rem"
});

const BusinessCardChip = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-chip"
})({
  display: "inline-flex",
  height: "1.75rem",
  maxWidth: "136px",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  borderRadius: "var(--bfrs-radius-pill)",
  borderWidth: "1px",
  borderColor: "var(--bfrs-dcl-color-neutral-stroke-2)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "7px",
  paddingRight: "7px",
  fontSize: "11.667px",
  fontWeight: 500,
  lineHeight: 1,
  color: "var(--bfrs-dcl-color-business-card-text)",
  "--tw-shadow": "var(--bfrs-shadow-low)",
  "--tw-shadow-colored": "var(--bfrs-shadow-low)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const BusinessCardChipIcon = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-chipIcon"
})({
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary-bright) / var(--tw-text-opacity, 1))"
});

const BusinessCardChipLabel = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-chipLabel"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const BusinessCardBody = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-body"
})({
  paddingLeft: "44px",
  paddingRight: "44px",
  paddingBottom: "2rem",
  paddingTop: "8px"
});

const BusinessCardDetailGrid = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-detailGrid"
})({
  display: "grid",
  gap: "14px"
});

const BusinessCardDetailSkeletonRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-detailSkeletonRow"
})({
  display: "grid",
  gridTemplateColumns: "minmax(0,92px) auto minmax(0,1fr)",
  alignItems: "center",
  MozColumnGap: "1.5rem",
  columnGap: "1.5rem"
});

const BusinessCardDetailSkeleton = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCard-detailSkeleton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { width: string } }>(({ ownerState }) => ({
  "&&": {
    height: "1rem",
    width: ownerState.width
  }
}));

const BusinessCardSection = styled("section", {
  label: "AgenticBusinessInfoDisplayCard-section",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { spaced: boolean } }>(({ ownerState }) => [
  ownerState.spaced && { marginTop: "1rem" }
]);

const BusinessCardSectionTitle = styled("h4", {
  label: "AgenticBusinessInfoDisplayCard-sectionTitle"
})({
  marginBottom: ".75rem",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  lineHeight: 1.5,
  letterSpacing: ".07em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
});

const BusinessCardDefinitionList = styled("dl", {
  label: "AgenticBusinessInfoDisplayCard-definitionList"
})({
  display: "grid",
  gap: "14px",
  fontSize: "11.667px",
  lineHeight: 1.5
});

const BusinessCardDetailRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCard-detailRow"
})({
  display: "grid",
  gridTemplateColumns: "minmax(0,92px) auto minmax(0,1fr)",
  alignItems: "flex-start",
  MozColumnGap: "1.5rem",
  columnGap: "1.5rem"
});

const BusinessCardDetailLabel = styled("dt", {
  label: "AgenticBusinessInfoDisplayCard-detailLabel"
})({
  color: "var(--bfrs-dcl-color-business-card-muted)"
});

const BusinessCardDetailColon = styled("span", {
  label: "AgenticBusinessInfoDisplayCard-detailColon"
})({
  color: "var(--bfrs-dcl-color-business-card-muted)"
});

const BusinessCardDetailValue = styled("dd", {
  label: "AgenticBusinessInfoDisplayCard-detailValue"
})({
  minWidth: "0px",
  fontWeight: 600,
  color: "var(--bfrs-dcl-color-business-card-text)"
});

function isMissingDisplayNode(node: ReactNode) {
  return node === undefined || node === null || node === false || (typeof node === "string" && node.trim().length === 0);
}

function BusinessInfoDetailSkeletonRows({ rows = DEFAULT_BUSINESS_INFO_SKELETON_ROWS }: { rows?: number }) {
  return (
    <BusinessCardDetailGrid aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <BusinessCardDetailSkeletonRow key={index}>
          <BusinessCardDetailSkeleton animated={false} variant="text" ownerState={{ width: "100%" }} />
          <BusinessCardDetailSkeleton animated={false} variant="text" ownerState={{ width: ".25rem" }} />
          <BusinessCardDetailSkeleton
            animated={false}
            variant="text"
            ownerState={{ width: skeletonValueWidths[index % skeletonValueWidths.length] }}
          />
        </BusinessCardDetailSkeletonRow>
      ))}
    </BusinessCardDetailGrid>
  );
}

export const BusinessInfoDisplayCard = forwardRef<HTMLDivElement, BusinessInfoDisplayCardProps>(
  (
    {
      className,
      businessName,
      subtitle,
      initials,
      logoSrc,
      logoAlt,
      chips,
      sections,
      background,
      gradient,
      headerBackground,
      headerGradient,
      style,
      ...props
    },
    ref
  ) => {
    const avatarInitials = typeof initials === "string" && initials.trim().length > 0 ? initials.trim() : undefined;
    const hasBusinessName = !isMissingDisplayNode(businessName);
    const isBootstrapLoading = !hasBusinessName && sections.length === 0;
    const resolvedChips = chips ?? defaultBusinessInfoChips;
    const dynamicStyle = {
      "--bfrs-business-card-background": gradient ?? background ?? "var(--bfrs-gradient-business-info-card)",
      "--business-card-header-background": headerGradient ?? headerBackground ?? "var(--bfrs-gradient-business-card-header)",
      ...style
    } as CSSProperties;

    return (
      <BusinessCardRoot ref={ref} className={className} style={dynamicStyle} {...props}>
        <BusinessCardHeader>
          {isBootstrapLoading ? (
            <BusinessCardAvatarSkeleton animated={false} variant="avatar" />
          ) : logoSrc ? (
            <BusinessCardLogoBox>
              <BusinessCardLogoImage src={logoSrc} alt={logoAlt ?? (typeof businessName === "string" ? businessName : "")} />
            </BusinessCardLogoBox>
          ) : avatarInitials ? (
            <BusinessCardLogoBox>
              <BusinessCardInitials>{avatarInitials}</BusinessCardInitials>
            </BusinessCardLogoBox>
          ) : (
            <BusinessCardAvatarSkeleton animated={false} variant="avatar" />
          )}
          {avatarInitials && !logoSrc && <BusinessCardSrOnly>{avatarInitials}</BusinessCardSrOnly>}
          <BusinessCardTitleGroup>
            {hasBusinessName ? (
              <BusinessCardName>{businessName}</BusinessCardName>
            ) : (
              <BusinessCardNameSkeleton animated={false} variant="text" />
            )}
            {isBootstrapLoading ? (
              <BusinessCardSubtitleSkeleton animated={false} variant="text" />
            ) : subtitle ? (
              <BusinessCardSubtitle>{subtitle}</BusinessCardSubtitle>
            ) : null}
          </BusinessCardTitleGroup>
          {isBootstrapLoading ? (
            <BusinessCardChipsLoadingRow>
              <BusinessCardChipSkeleton animated={false} variant="text" ownerState={{ width: "118px" }} />
              <BusinessCardChipSkeleton animated={false} variant="text" ownerState={{ width: "98px" }} />
            </BusinessCardChipsLoadingRow>
          ) : resolvedChips.length > 0 && (
            <BusinessCardChipsRow>
              {resolvedChips.map((chip, index) => {
                const isChipLabelMissing = isMissingDisplayNode(chip.label);

                if (isChipLabelMissing) {
                  return (
                    <BusinessCardChipSkeleton
                      key={index}
                      animated={false}
                      variant="text"
                      ownerState={{ width: chipSkeletonWidths[index % chipSkeletonWidths.length] }}
                    />
                  );
                }

                return (
                  <BusinessCardChip key={index}>
                    {chip.icon && <BusinessCardChipIcon>{chip.icon}</BusinessCardChipIcon>}
                    <BusinessCardChipLabel>{chip.label}</BusinessCardChipLabel>
                  </BusinessCardChip>
                );
              })}
            </BusinessCardChipsRow>
          )}
        </BusinessCardHeader>
        <BusinessCardBody>
          {sections.length === 0 ? (
            <BusinessInfoDetailSkeletonRows rows={DEFAULT_BUSINESS_INFO_SKELETON_ROWS} />
          ) : sections.map((section, sectionIndex) => (
            <BusinessCardSection key={sectionIndex} ownerState={{ spaced: sectionIndex > 0 }}>
              {section.title && <BusinessCardSectionTitle>{section.title}</BusinessCardSectionTitle>}
              <BusinessCardDefinitionList>
                {section.items.map((item, itemIndex) => {
                  const isItemValueMissing = isMissingDisplayNode(item.value);

                  return (
                    <BusinessCardDetailRow key={itemIndex}>
                      <BusinessCardDetailLabel>
                        {isItemValueMissing ? (
                          <BusinessCardDetailSkeleton animated={false} variant="text" ownerState={{ width: "100%" }} />
                        ) : item.label}
                      </BusinessCardDetailLabel>
                      <BusinessCardDetailColon aria-hidden="true">
                        {isItemValueMissing ? (
                          <BusinessCardDetailSkeleton animated={false} variant="text" ownerState={{ width: ".25rem" }} />
                        ) : ":"}
                      </BusinessCardDetailColon>
                      <BusinessCardDetailValue>
                        {isItemValueMissing ? (
                          <BusinessCardDetailSkeleton
                            animated={false}
                            variant="text"
                            ownerState={{ width: skeletonValueWidths[(sectionIndex + itemIndex) % skeletonValueWidths.length] }}
                          />
                        ) : item.value}
                      </BusinessCardDetailValue>
                    </BusinessCardDetailRow>
                  );
                })}
              </BusinessCardDefinitionList>
            </BusinessCardSection>
          ))}
        </BusinessCardBody>
      </BusinessCardRoot>
    );
  }
);

BusinessInfoDisplayCard.displayName = "BusinessInfoDisplayCard";
